/**
 * F-devis-panel-i18n (CEO 2026-09-18) — os nomes das peças, métodos e notas
 * vêm do DEVIS (documento em francês): são DADOS, não chaves de i18n, e por
 * isso nunca passavam pela tradução. Este dicionário traduz só a EXIBIÇÃO
 * (o dado gravado permanece o original do devis).
 *
 * Termos desconhecidos ficam como estão (melhor original que errado).
 * FR = original. "ALU" (alumínio) é preservado como sufixo.
 *
 * CEO 2026-10-26 — movido para @eurostorm/shared: a fatura da Brazilian Storm
 * (PDF em inglês, gerado na API) usa as mesmas traduções da tela.
 */

/** Idiomas do Gestion (mesma lista de apps/web/src/i18n/messages.ts). */
export type DevisLocale = "fr" | "pt" | "en" | "es" | "uk";
type Locale = DevisLocale;

type Tr = { pt: string; en: string; es: string; uk: string };

const PANELS: Record<string, Tr> = {
  "aile avant gauche": {
    pt: "Para-lama dianteiro esquerdo",
    en: "Front left fender",
    es: "Aleta delantera izquierda",
    uk: "Переднє ліве крило",
  },
  "aile avant droite": {
    pt: "Para-lama dianteiro direito",
    en: "Front right fender",
    es: "Aleta delantera derecha",
    uk: "Переднє праве крило",
  },
  "aile arrière gauche": {
    pt: "Para-lama traseiro esquerdo",
    en: "Rear left fender",
    es: "Aleta trasera izquierda",
    uk: "Заднє ліве крило",
  },
  "aile arrière droite": {
    pt: "Para-lama traseiro direito",
    en: "Rear right fender",
    es: "Aleta trasera derecha",
    uk: "Заднє праве крило",
  },
  "porte avant gauche": {
    pt: "Porta dianteira esquerda",
    en: "Front left door",
    es: "Puerta delantera izquierda",
    uk: "Передні ліві двері",
  },
  "porte avant droite": {
    pt: "Porta dianteira direita",
    en: "Front right door",
    es: "Puerta delantera derecha",
    uk: "Передні праві двері",
  },
  "porte arrière gauche": {
    pt: "Porta traseira esquerda",
    en: "Rear left door",
    es: "Puerta trasera izquierda",
    uk: "Задні ліві двері",
  },
  "porte arrière droite": {
    pt: "Porta traseira direita",
    en: "Rear right door",
    es: "Puerta trasera derecha",
    uk: "Задні праві двері",
  },
  pavillon: { pt: "Teto", en: "Roof", es: "Techo", uk: "Дах" },
  capot: { pt: "Capô", en: "Hood", es: "Capó", uk: "Капот" },
  coffre: { pt: "Porta-malas", en: "Trunk", es: "Maletero", uk: "Багажник" },
  hayon: { pt: "Tampa traseira", en: "Tailgate", es: "Portón trasero", uk: "Задні двері" },
  casquette: { pt: "Aba do teto", en: "Roof visor", es: "Visera del techo", uk: "Козирок даху" },
  "brancard gauche": {
    pt: "Coluna esquerda",
    en: "Left pillar",
    es: "Columna izquierda",
    uk: "Ліва стійка",
  },
  "brancard droit": {
    pt: "Coluna direita",
    en: "Right pillar",
    es: "Columna derecha",
    uk: "Права стійка",
  },
  "pare-brise": { pt: "Para-brisa", en: "Windshield", es: "Parabrisas", uk: "Лобове скло" },
  "bas de caisse gauche": {
    pt: "Saia lateral esquerda",
    en: "Left rocker panel",
    es: "Estribo izquierdo",
    uk: "Лівий поріг",
  },
  "bas de caisse droit": {
    pt: "Saia lateral direita",
    en: "Right rocker panel",
    es: "Estribo derecho",
    uk: "Правий поріг",
  },
  "custode gauche": {
    pt: "Lateral traseira esquerda",
    en: "Left quarter panel",
    es: "Panel trasero izquierdo",
    uk: "Задня бокова панель ліва",
  },
  "custode droite": {
    pt: "Lateral traseira direita",
    en: "Right quarter panel",
    es: "Panel trasero derecho",
    uk: "Задня бокова панель права",
  },
  // Variantes abreviadas do layout CHIR-AUTO ("Porte AVG", "Aile ARD"…).
  "porte avg": {
    pt: "Porta diant. esq.",
    en: "Front left door",
    es: "Puerta del. izq.",
    uk: "Передні ліві двері",
  },
  "porte avd": {
    pt: "Porta diant. dir.",
    en: "Front right door",
    es: "Puerta del. der.",
    uk: "Передні праві двері",
  },
  "porte arg": {
    pt: "Porta tras. esq.",
    en: "Rear left door",
    es: "Puerta tras. izq.",
    uk: "Задні ліві двері",
  },
  "porte ard": {
    pt: "Porta tras. dir.",
    en: "Rear right door",
    es: "Puerta tras. der.",
    uk: "Задні праві двері",
  },
  "aile avg": {
    pt: "Para-lama diant. esq.",
    en: "Front left fender",
    es: "Aleta del. izq.",
    uk: "Переднє ліве крило",
  },
  "aile avd": {
    pt: "Para-lama diant. dir.",
    en: "Front right fender",
    es: "Aleta del. der.",
    uk: "Переднє праве крило",
  },
  "aile arg": {
    pt: "Para-lama tras. esq.",
    en: "Rear left fender",
    es: "Aleta tras. izq.",
    uk: "Заднє ліве крило",
  },
  "aile ard": {
    pt: "Para-lama tras. dir.",
    en: "Rear right fender",
    es: "Aleta tras. der.",
    uk: "Заднє праве крило",
  },
};

const METHODS: Record<string, Tr> = {
  echange: { pt: "Troca", en: "Replace", es: "Cambio", uk: "Заміна" },
  peinture: { pt: "Pintura", en: "Paint", es: "Pintura", uk: "Фарбування" },
  remplacement: { pt: "Substituição", en: "Replacement", es: "Sustitución", uk: "Заміна" },
  rien: { pt: "Nada", en: "None", es: "Nada", uk: "Нічого" },
  // DSP/DAP/BT/Traditionnel são jargão do métier — ficam como no devis.
};

/**
 * F-depose-i18n (CEO 2026-09-20, achado no perfil do montador) — a linha
 * "Dépose/Repose" do devis é a LISTA DE TRABALHO do montador: é o que ele
 * desmonta e remonta. Vinha crua do PDF, em francês, na tela dele em
 * português. Cada item é traduzido; o que não está aqui fica como no devis.
 */
const DEPOSE: Record<string, Tr> = {
  "ciel de toit": {
    pt: "Forro do teto",
    en: "Headliner",
    es: "Techo interior",
    uk: "Обшивка даху",
  },
  "garniture de pavillon": {
    pt: "Forro do teto",
    en: "Headliner",
    es: "Techo interior",
    uk: "Обшивка даху",
  },
  "toit ouvrant": { pt: "Teto solar", en: "Sunroof", es: "Techo solar", uk: "Люк даху" },
  "barre de toit": {
    pt: "Barra de teto",
    en: "Roof bar",
    es: "Barra de techo",
    uk: "Рейлінг даху",
  },
  "barres de toit": {
    pt: "Barras de teto",
    en: "Roof bars",
    es: "Barras de techo",
    uk: "Рейлінги даху",
  },
  "becquet de hayon": {
    pt: "Aerofólio da tampa traseira",
    en: "Tailgate spoiler",
    es: "Alerón del portón",
    uk: "Спойлер задніх дверей",
  },
  "garniture hayon": {
    pt: "Forro da tampa traseira",
    en: "Tailgate trim",
    es: "Guarnecido del portón",
    uk: "Обшивка задніх дверей",
  },
  "garniture de hayon": {
    pt: "Forro da tampa traseira",
    en: "Tailgate trim",
    es: "Guarnecido del portón",
    uk: "Обшивка задніх дверей",
  },
  "garniture de porte": {
    pt: "Forro da porta",
    en: "Door trim",
    es: "Guarnecido de puerta",
    uk: "Обшивка дверей",
  },
  "garniture de coffre": {
    pt: "Forro do porta-malas",
    en: "Trunk trim",
    es: "Guarnecido del maletero",
    uk: "Обшивка багажника",
  },
  "pare-chocs avant": {
    pt: "Para-choque dianteiro",
    en: "Front bumper",
    es: "Parachoques delantero",
    uk: "Передній бампер",
  },
  "pare-chocs arrière": {
    pt: "Para-choque traseiro",
    en: "Rear bumper",
    es: "Parachoques trasero",
    uk: "Задній бампер",
  },
  "feu arrière": {
    pt: "Lanterna traseira",
    en: "Tail light",
    es: "Piloto trasero",
    uk: "Задній ліхтар",
  },
  "optique avant": { pt: "Farol", en: "Headlight", es: "Faro", uk: "Фара" },
  rétroviseur: { pt: "Retrovisor", en: "Mirror", es: "Retrovisor", uk: "Дзеркало" },
  antenne: { pt: "Antena", en: "Antenna", es: "Antena", uk: "Антена" },
  aileron: { pt: "Aerofólio", en: "Spoiler", es: "Alerón", uk: "Спойлер" },
  calandre: { pt: "Grade", en: "Grille", es: "Rejilla", uk: "Решітка" },
  "essuie-glace": {
    pt: "Limpador de para-brisa",
    en: "Wiper",
    es: "Limpiaparabrisas",
    uk: "Двірник",
  },
  poignée: { pt: "Maçaneta", en: "Handle", es: "Manilla", uk: "Ручка" },
  baguette: { pt: "Friso", en: "Trim strip", es: "Moldura", uk: "Молдинг" },
  moulure: { pt: "Moldura", en: "Molding", es: "Moldura", uk: "Молдинг" },
  siège: { pt: "Banco", en: "Seat", es: "Asiento", uk: "Сидіння" },
  sièges: { pt: "Bancos", en: "Seats", es: "Asientos", uk: "Сидіння" },
  vitre: { pt: "Vidro", en: "Window", es: "Cristal", uk: "Скло" },
  joint: { pt: "Borracha", en: "Seal", es: "Junta", uk: "Ущільнювач" },
};

const NOTES: Record<string, Tr> = {
  "réserve peinture": {
    pt: "Reserva de pintura",
    en: "Paint reserve",
    es: "Reserva de pintura",
    uk: "Резерв фарбування",
  },
  réserve: { pt: "Reserva", en: "Reserve", es: "Reserva", uk: "Резерв" },
};

function lookup(map: Record<string, Tr>, raw: string, locale: Locale): string {
  if (locale === "fr") return raw;
  const key = raw.trim().toLowerCase();
  const hit = map[key];
  if (!hit) return raw;
  return hit[locale as keyof Tr] ?? raw;
}

/** Traduz o nome da peça preservando o sufixo "ALU" (alumínio). */
export function trPanel(raw: string, locale: Locale): string {
  if (locale === "fr") return raw;
  const m = raw.trim().match(/^(.*?)(\s+ALU)?$/i);
  const base = (m?.[1] ?? raw).trim();
  const alu = m?.[2] ? " ALU" : "";
  const translated = lookup(PANELS, base, locale);
  return translated === base ? raw : translated + alu;
}

export function trMethod(raw: string, locale: Locale): string {
  return lookup(METHODS, raw, locale);
}

export function trNote(raw: string, locale: Locale): string {
  return lookup(NOTES, raw, locale);
}

/**
 * Traduz a linha inteira de dépose/repose, item a item. Os itens vêm
 * separados por " - " (padrão do eDSP), às vezes por "/" ou ",". Procura
 * primeiro no dicionário de dépose e depois no de peças (capot, hayon,
 * pare-brise… já estão lá).
 */
export function trDepose(raw: string, locale: Locale): string {
  if (locale === "fr") return raw;
  return raw
    .split(/\s*[-–/,;]\s+|\s+[-–/,;]\s*/)
    .map((item) => {
      const limpo = item.trim();
      if (!limpo) return limpo;
      const doDepose = lookup(DEPOSE, limpo, locale);
      if (doDepose !== limpo) return doDepose;
      return lookup(PANELS, limpo, locale);
    })
    .filter((s) => s.length > 0)
    .join(" · ");
}
